import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { get, ref, update } from 'firebase/database';
import { ref as storageRef, uploadBytes, getDownloadURL } from 'firebase/storage';
import {
  FaAngleDown,
  FaCheckCircle,
  FaFileImage,
  FaLink,
  FaMagic,
  FaPaperclip,
  FaPlus,
  FaRedo,
  FaSave,
  FaTrash,
  FaUpload,
  FaYoutube,
} from 'react-icons/fa';
import { db, storage } from '../../config/firebase';
import { normalizeLessonVideos } from '../../utils/lessonMedia';
import {
  createLessonPlannerDraft,
  fetchLessonPlannerDraft,
  generateAssignmentsAndCover,
  generateCourseStructure,
  generateLessonForSubtopic,
  generateLessonImage,
  generateLessons,
  publishLessonPlannerDraft,
  regenerateDraftItem,
  saveLessonPlannerDraft,
  uploadLessonPlannerAsset,
} from '../../services/lessonPlannerService';
import { lessonReaderUrlSegment, sortLessonsBySession } from '../../utils/lessonReader';
import Button from '../ui/Button';
import {
  WorkspaceEmptyState,
  WorkspacePageLoader,
  WorkspaceRail,
  WorkspaceSelectableList,
  WorkspaceShell,
  WorkspaceStatusChip,
} from '../ui/Workspace';

const emptyDraft = null;

const getCourseStatusClass = (status) => {
  switch (status) {
    case 'active':
      return 'bg-green-100 text-green-800';
    case 'inactive':
      return 'bg-yellow-100 text-yellow-800';
    default:
      return 'bg-gray-100 text-gray-700';
  }
};

const getStepStatusClass = (status) => {
  switch (status) {
    case 'running':
      return 'bg-amber-100 text-amber-800';
    case 'succeeded':
    case 'generated':
      return 'bg-pink-100 text-pink-800';
    case 'failed':
      return 'bg-red-100 text-red-700';
    case 'edited':
      return 'bg-yellow-100 text-yellow-700';
    case 'approved':
      return 'bg-green-100 text-green-700';
    case 'published':
      return 'bg-emerald-100 text-emerald-700';
    default:
      return 'bg-gray-100 text-gray-600';
  }
};

const getStepStatusLabel = (status) => {
  switch (status) {
    case 'running':
      return 'Running';
    case 'succeeded':
      return 'Succeeded';
    case 'failed':
      return 'Failed';
    case 'generated':
      return 'Generated';
    case 'edited':
      return 'Edited';
    case 'published':
      return 'Published';
    case 'not_started':
      return 'Not started';
    default:
      return status || 'Unknown';
  }
};

const emptyLessonContent = () => ({
  overview: '',
  tutorialIntro: '',
  tutorialSummary: '',
  learningOutcomes: [],
  lessonFlow: [],
  materials: [],
  activities: [],
  assessment: '',
  homePractice: '',
  instructorNotes: '',
  safetyAndHygiene: [],
  studentTutorial: [],
});

const normalizeLessonContent = (content) => ({
  ...emptyLessonContent(),
  ...(content || {}),
  tutorialIntro: typeof content?.tutorialIntro === 'string' ? content.tutorialIntro : '',
  tutorialSummary: typeof content?.tutorialSummary === 'string' ? content.tutorialSummary : '',
  learningOutcomes: Array.isArray(content?.learningOutcomes)
    ? content.learningOutcomes
    : [],
  lessonFlow: Array.isArray(content?.lessonFlow) ? content.lessonFlow : [],
  materials: Array.isArray(content?.materials) ? content.materials : [],
  activities: Array.isArray(content?.activities) ? content.activities : [],
  safetyAndHygiene: Array.isArray(content?.safetyAndHygiene)
    ? content.safetyAndHygiene
    : [],
  studentTutorial: Array.isArray(content?.studentTutorial) ? content.studentTutorial : [],
});

const syncLessonWithContent = (lesson, contentPatch = {}) => {
  const content = {
    ...normalizeLessonContent(lesson?.content),
    ...contentPatch,
  };

  return {
    ...lesson,
    content,
    description: content.overview || '',
    learningObjectives: content.learningOutcomes || [],
    practicalActivities: content.activities || [],
    imageRequirements:
      lesson?.imageRequirements ||
      (content.lessonFlow || []).join('\n'),
  };
};

const createTopic = (index) => ({
  id: `topic-manual-${Date.now()}-${index}`,
  title: '',
  description: '',
  subtopics: [
    {
      id: `subtopic-manual-${Date.now()}-${index}-1`,
      title: '',
      description: '',
    },
  ],
});

const createLesson = (index) => ({
  id: `lesson-manual-${Date.now()}-${index}`,
  sessionNumber: index + 1,
  sessionLabel: `Session ${index + 1}`,
  sessionType: 'blended',
  topicId: '',
  subtopicId: '',
  title: '',
  description: '',
  learningObjectives: [],
  practicalActivities: [],
  extraReadings: [],
  plannedDate: '',
  plannedStartTime: '',
  plannedEndTime: '',
  estimatedMinutes: 240,
  assignment: {
    title: '',
    brief: '',
    instructions: '',
    deliverables: '',
    rubric: '',
  },
  content: emptyLessonContent(),
  imagePrompt: '',
  imageRequirements: '',
  imageUrl: '',
  coverImage: '',
  videoUrl: '',
  lessonVideos: [],
  attachments: [],
  assignmentUrl: '',
  tuteUrl: '',
});

const PLANNER_TABS = [
  { id: 'overview', label: 'Overview' },
  { id: 'structure', label: 'Modules' },
  { id: 'lessons', label: 'Sessions' },
  { id: 'media', label: 'Images' },
];

const inputPlanner =
  'w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 focus:border-pink-500 focus:outline-none focus:ring-1 focus:ring-pink-500';

const textareaPlanner =
  'w-full resize-y rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 focus:border-pink-500 focus:outline-none focus:ring-1 focus:ring-pink-500';

const computeScheduleFit = (draftValue, lessons) => {
  const totalSessions =
    Number(draftValue?.context?.planning?.totalSessions || 0) || 0;
  const overflowCount = Math.max(0, lessons.length - totalSessions);

  return {
    totalSessions,
    generatedLessons: lessons.length,
    overflowCount,
    lastPlannedLessonDate:
      lessons
        .map((lesson) => lesson.sessionLabel)
        .filter(Boolean)
        .sort()
        .slice(-1)[0] || null,
  };
};

const parseOutlineModules = (outline) =>
  String(outline || '')
    .split(/\r?\n/)
    .map((line) =>
      line
        .trim()
        .replace(/^[-*\u2022]\s+/, '')
        .replace(/^\d+[.)]\s+/, '')
        .trim(),
    )
    .filter(Boolean)
    .map((title, index) => ({
      id: `topic-outline-${Date.now()}-${index}`,
      title,
      description: title,
      subtopics: [
        {
          id: `subtopic-outline-${Date.now()}-${index}-1`,
          title,
          description: title,
        },
      ],
    }));

const PlannerCourseNavigatorItem = ({ course, isSelected }) => (
  <div className="space-y-1.5">
    <div className="flex items-start justify-between gap-2">
      <div className="min-w-0">
        <p className="truncate text-xs font-semibold text-stone-900 sm:text-sm">
          {course.name || 'Untitled course'}
        </p>
        <p className="truncate text-[11px] text-stone-500 sm:text-xs">
          {course.courseCode || 'No code'} · {course.academicLevel || 'Level not set'}
        </p>
      </div>
      <WorkspaceStatusChip status={course.status || 'draft'} label={course.status || 'draft'} />
    </div>
    <div className="grid grid-cols-2 gap-2 text-[11px] text-stone-600 sm:text-xs">
      <span>{course.lessons?.length || 0} sessions</span>
      <span>{course.batches?.length || 0} batches</span>
    </div>
    {isSelected ? (
      <p className="text-[11px] font-medium uppercase tracking-[0.12em] text-[#b91c66]">
        Selected
      </p>
    ) : null}
  </div>
);

const LessonPlanner = () => {
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCourseId, setSelectedCourseId] = useState(null);
  const [draft, setDraft] = useState(emptyDraft);
  const [busyAction, setBusyAction] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    loadCourses();
  }, []);

  useEffect(() => {
    if (!selectedCourseId) {
      setDraft(emptyDraft);
      return;
    }

    loadDraft(selectedCourseId);
  }, [selectedCourseId]);

  const loadCourses = async () => {
    try {
      setLoading(true);
      const courseSnapshot = await get(ref(db, 'courses'));
      const enrollmentSnapshot = await get(ref(db, 'enrollments'));
      const courseMap = courseSnapshot.val() || {};
      const enrollments = Object.values(enrollmentSnapshot.val() || {});

      const nextCourses = Object.entries(courseMap)
        .map(([id, course]) => {
          const activeEnrollments = enrollments.filter(
            (enrollment) =>
              enrollment.courseId === id && enrollment.status === 'active',
          );

          return {
            id,
            ...course,
            lessons: Array.isArray(course.lessons) ? course.lessons : [],
            batches: Array.isArray(course.batches) ? course.batches : [],
            totalStudents: activeEnrollments.length,
          };
        })
        .sort((a, b) =>
          String(a.name || a.title || '').localeCompare(
            String(b.name || b.title || ''),
          ),
        );

      setCourses(nextCourses);
      setSelectedCourseId((current) =>
        current && nextCourses.some((course) => course.id === current)
          ? current
          : nextCourses[0]?.id || null,
      );
    } catch (loadError) {
      console.error('Error loading courses:', loadError);
      setError('Failed to load courses.');
    } finally {
      setLoading(false);
    }
  };

  const loadDraft = async (courseId) => {
    try {
      setError('');
      const nextDraft = await fetchLessonPlannerDraft(courseId);
      setDraft(nextDraft || emptyDraft);
    } catch (draftError) {
      console.error('Error loading draft:', draftError);
      setDraft(emptyDraft);
    }
  };

  const selectedCourse = useMemo(
    () => courses.find((course) => course.id === selectedCourseId) || null,
    [courses, selectedCourseId],
  );

  const draftTopics = draft?.structure?.topics;
  const draftLessonItems = draft?.lessons?.items;
  const canPublishDraft = Boolean(
    selectedCourse &&
      draft &&
      Array.isArray(draftTopics) &&
      draftTopics.length > 0 &&
      Array.isArray(draftLessonItems) &&
      draftLessonItems.length > 0,
  );

  const runAction = async (label, action) => {
    try {
      setBusyAction(label);
      setError('');
      await action();
    } catch (actionError) {
      console.error(label, actionError);
      setError(actionError?.message || 'Something went wrong.');
    } finally {
      setBusyAction('');
    }
  };

  const persistDraft = async (nextDraft, successStatusKey = '') => {
    if (!selectedCourseId || !nextDraft) return;

    const draftToSave = {
      ...nextDraft,
      status: successStatusKey
        ? {
            ...(nextDraft.status || {}),
            steps: {
              ...(nextDraft.status?.steps || {}),
              [successStatusKey]: 'edited',
            },
          }
        : nextDraft.status,
    };

    setDraft(draftToSave);
    await saveLessonPlannerDraft(selectedCourseId, draftToSave);
  };

  if (loading) {
    return <WorkspacePageLoader />;
  }

  return (
    <WorkspaceShell
      title="AI Lesson Planner"
      description="A fresh planning workflow: choose a course from the navigator, shape modules and sessions in one focused workspace, then save or publish from the top action bar."
    >
      {error && (
        <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          {error}
        </div>
      )}

      <div className="grid grid-cols-1 gap-4 xl:grid-cols-[minmax(250px,300px)_minmax(0,1fr)] xl:items-start">
        <aside className="min-w-0 space-y-4 xl:sticky xl:top-4 xl:self-start">
          <WorkspaceRail
            title="Course Navigator"
            description="Switch courses here. The planner updates instantly with the selected course context."
          >
            <WorkspaceSelectableList
              items={courses}
              selectedId={selectedCourseId || ''}
              onSelect={(id) => setSelectedCourseId(id || null)}
              getKey={(course) => course.id}
              emptyTitle="No courses available"
              emptyMessage="Add your first course, then return to plan modules and sessions."
              renderItem={(course, selected) => (
                <PlannerCourseNavigatorItem course={course} isSelected={selected} />
              )}
            />
          </WorkspaceRail>
        </aside>

        <div className="min-w-0 space-y-3.5">
          {selectedCourse ? (
            <div className="rounded-2xl border border-stone-200 bg-white px-4 py-3.5 shadow-[0_14px_30px_-24px_rgba(28,25,23,0.35)]">
              <div className="flex flex-col gap-2.5 lg:flex-row lg:items-center lg:justify-between">
                <div>
                  <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-stone-500">
                    Active planning course
                  </p>
                  <h2 className="mt-1 text-lg font-semibold tracking-tight text-stone-950 sm:text-xl">
                    {selectedCourse.name || 'Untitled course'}
                  </h2>
                  <p className="mt-0.5 text-xs text-stone-600 sm:text-sm">
                    {selectedCourse.courseCode || 'No code'} ·{' '}
                    {selectedCourse.academicLevel || 'Level not set'} ·{' '}
                    {selectedCourse.lessons?.length || 0} published sessions
                  </p>
                </div>
                <div className="flex flex-wrap items-center gap-1.5 sm:gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() =>
                      runAction('saveDraft', async () => {
                        await persistDraft(draft);
                      })
                    }
                    disabled={!draft || busyAction === 'saveDraft' || busyAction === 'publish'}
                    title={!draft ? 'Create a draft from the Overview tab first' : undefined}
                  >
                    <span className="inline-flex items-center gap-2">
                      <FaSave className="text-xs" />
                      {busyAction === 'saveDraft' ? 'Saving…' : 'Save draft'}
                    </span>
                  </Button>
                  <Button
                    size="sm"
                    onClick={() =>
                      runAction('publish', async () => {
                        await publishLessonPlannerDraft(selectedCourse.id);
                        await loadDraft(selectedCourse.id);
                        await loadCourses();
                      })
                    }
                    disabled={
                      !canPublishDraft || busyAction === 'saveDraft' || busyAction === 'publish'
                    }
                    title={
                      !draft
                        ? 'Create a draft from the Overview tab first'
                        : !canPublishDraft
                          ? 'Add modules and sessions before publishing'
                          : undefined
                    }
                  >
                    <span className="inline-flex items-center gap-2">
                      <FaCheckCircle className="text-xs" />
                      {busyAction === 'publish' ? 'Publishing…' : 'Publish'}
                    </span>
                  </Button>
                </div>
              </div>
            </div>
          ) : courses.length === 0 ? (
            <WorkspaceEmptyState
              title="No courses yet"
              message="Add a course first, then return here to plan lessons."
            />
          ) : (
            <WorkspaceEmptyState
              title="No course selected"
              message="Choose a course from the navigator to open the planner workspace."
            />
          )}

          {selectedCourse ? (
            <PlannerWorkspace
              course={selectedCourse}
              draft={draft}
              busyAction={busyAction}
              runAction={runAction}
              persistDraft={persistDraft}
              refreshDraft={() => loadDraft(selectedCourse.id)}
              refreshCourses={loadCourses}
              courseId={selectedCourse.id}
            />
          ) : null}
        </div>
      </div>
    </WorkspaceShell>
  );
};

const buildCourseFormState = (c) => {
  const pc = c?.planningCoverage || {};
  return {
    name: c?.name || '',
    courseCode: c?.courseCode || '',
    type: c?.type || 'physical',
    academicLevel: c?.academicLevel || 'certificate',
    description: c?.description || '',
    status: c?.status || 'draft',
    durationMonths: c?.duration?.isOneDay ? 0 : Number(c?.duration?.months || 1),
    isOneDay: Boolean(c?.duration?.isOneDay),
    coverageTotal: pc.totalHours != null && pc.totalHours !== '' ? String(pc.totalHours) : '',
    coverageTheory: pc.theoryHours != null && pc.theoryHours !== '' ? String(pc.theoryHours) : '',
    coveragePractical:
      pc.practicalHours != null && pc.practicalHours !== '' ? String(pc.practicalHours) : '',
  };
};

const parseOptionalHours = (value) => {
  const t = String(value ?? '').trim();
  if (t === '') return null;
  const n = Number(t);
  return Number.isFinite(n) && n >= 0 ? n : null;
};

/** Reader shortcuts + session list — top of planner sidebar. */
const PlannerSidebarReaderPanel = ({ courseId, lessons, navigate }) => {
  const sortedReaderLessons = useMemo(
    () => sortLessonsBySession(Array.isArray(lessons) ? lessons : []),
    [lessons],
  );

  if (!courseId) return null;

  return (
    <section className="rounded-lg border border-slate-200 bg-white p-3 shadow-sm sm:p-4">
      <p className="text-sm font-medium text-slate-800">View generated lessons</p>
      <p className="mt-1 text-xs text-slate-600">
        Opens the learner lesson reader (live data on the server). Publish first so it matches this
        draft.
      </p>
      {sortedReaderLessons.length > 0 ? (
        <>
          <div className="mt-3 flex flex-wrap gap-2">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => {
                const first = sortedReaderLessons[0];
                const seg = lessonReaderUrlSegment(lessons, first);
                navigate(`/courses/${courseId}/lessons/${seg}`);
              }}
            >
              Open first session (reader)
            </Button>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => navigate(`/courses/${courseId}`)}
            >
              Course page (all readers)
            </Button>
          </div>
          <details className="mt-3 text-sm">
            <summary className="cursor-pointer font-medium text-fuchsia-800">
              All sessions — open in reader
            </summary>
            <ul className="mt-2 max-h-44 space-y-1 overflow-y-auto border-t border-slate-200 pt-2">
              {sortedReaderLessons.map((l, idx) => (
                <li key={l.id != null ? String(l.id) : `session-${idx}`}>
                  <button
                    type="button"
                    className="text-left text-fuchsia-800 hover:underline"
                    onClick={() =>
                      navigate(
                        `/courses/${courseId}/lessons/${lessonReaderUrlSegment(lessons, l)}`,
                      )
                    }
                  >
                    {l.sessionNumber != null ? `Session ${l.sessionNumber}: ` : ''}
                    {l.title || 'Untitled'}
                  </button>
                </li>
              ))}
            </ul>
          </details>
        </>
      ) : (
        <p className="mt-3 text-xs text-slate-500">Add sessions in the planner to open them here.</p>
      )}
    </section>
  );
};

const PlannerWorkspace = ({
  course,
  courseId,
  draft,
  busyAction,
  runAction,
  persistDraft,
  refreshDraft,
  refreshCourses,
}) => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('overview');
  const [aiReferenceOutline, setAiReferenceOutline] = useState('');
  const draftSteps = draft?.status?.steps || {};
  const topics = Array.isArray(draft?.structure?.topics) ? draft.structure.topics : [];
  const lessons = useMemo(() => {
    const items = draft?.lessons?.items;
    return Array.isArray(items) ? items : [];
  }, [draft?.lessons?.items]);
  const scheduleFit = draft?.lessons?.scheduleFit || {};
  const courseCoverImage = draft?.assets?.courseCoverImage || {};
  const liveCourseCoverUrl = course.imageUrl || course.coverImage || '';
  const effectiveCourseCoverImage = {
    ...courseCoverImage,
    url: courseCoverImage.url || liveCourseCoverUrl,
    source: courseCoverImage.url
      ? courseCoverImage.source || 'draft'
      : liveCourseCoverUrl
        ? 'course'
        : '',
  };
  const generationJobs = draft?.generationJobs || {};

  const selectTab = (tabId) => {
    if (!draft && tabId !== 'overview') return;
    setActiveTab(tabId);
  };

  useEffect(() => {
    setActiveTab('overview');
  }, [course.id]);

  useEffect(() => {
    setAiReferenceOutline(draft?.context?.aiReferenceOutline || '');
  }, [draft?.context?.aiReferenceOutline, course.id]);

  const handleCreateDraft = async () => {
    await runAction('createDraft', async () => {
      await createLessonPlannerDraft(course.id);
      await refreshDraft();
      setActiveTab('structure');
    });
  };

  const updateTopics = (updater) => {
    const nextTopics = typeof updater === 'function' ? updater(topics) : updater;
    persistDraft(
      {
        ...draft,
        structure: {
          ...(draft?.structure || {}),
          topics: nextTopics,
        },
      },
      'structure',
    );
  };

  const updateLessons = (updater, stepKey = 'lessons') => {
    const nextLessons = typeof updater === 'function' ? updater(lessons) : updater;
    persistDraft(
      {
        ...draft,
        lessons: {
          ...(draft?.lessons || {}),
          items: nextLessons,
          scheduleFit: computeScheduleFit(draft, nextLessons),
        },
      },
      stepKey,
    );
  };

  const updateCoverImage = async (patch) => {
    await persistDraft(
      {
        ...draft,
        assets: {
          ...(draft?.assets || {}),
          courseCoverImage: {
            ...effectiveCourseCoverImage,
            ...patch,
          },
        },
      },
      'assignments',
    );
  };

  const clearAllTopics = () => {
    const ok = window.confirm(
      'Delete all modules and linked generated sessions from this draft?',
    );
    if (!ok) return;

    runAction('clearTopics', async () => {
      await persistDraft({
        ...draft,
        structure: {
          ...(draft?.structure || {}),
          topics: [],
        },
        lessons: {
          ...(draft?.lessons || {}),
          items: [],
          scheduleFit: computeScheduleFit(draft, []),
        },
        assets: {
          ...(draft?.assets || {}),
          courseCoverImage: {
            ...(draft?.assets?.courseCoverImage || {}),
            prompt: '',
            url: '',
          },
        },
        status: {
          ...(draft?.status || {}),
          steps: {
            ...(draft?.status?.steps || {}),
            structure: 'not_started',
            lessons: 'not_started',
            images: 'not_started',
            assignments: 'not_started',
          },
        },
        generationJobs: {
          ...(draft?.generationJobs || {}),
          structure: { status: 'idle' },
          lessons: { status: 'idle' },
          images: {},
          assignments: { status: 'idle' },
          coverImage: { status: 'idle' },
        },
      });
      await refreshDraft();
    });
  };

  const clearAllLessons = () => {
    const ok = window.confirm(
      'Delete all sessions, session images, assignments, and cover image from this draft?',
    );
    if (!ok) return;

    runAction('clearLessons', async () => {
      await persistDraft({
        ...draft,
        lessons: {
          ...(draft?.lessons || {}),
          items: [],
          scheduleFit: computeScheduleFit(draft, []),
        },
        assets: {
          ...(draft?.assets || {}),
          courseCoverImage: {
            ...(draft?.assets?.courseCoverImage || {}),
            prompt: '',
            url: '',
          },
        },
        status: {
          ...(draft?.status || {}),
          steps: {
            ...(draft?.status?.steps || {}),
            lessons: 'not_started',
            images: 'not_started',
            assignments: 'not_started',
          },
        },
        generationJobs: {
          ...(draft?.generationJobs || {}),
          lessons: { status: 'idle' },
          images: {},
          assignments: { status: 'idle' },
          coverImage: { status: 'idle' },
        },
      });
      await refreshDraft();
    });
  };

  const runGenerateAllLessons = () => {
    const ok = window.confirm(
      'This replaces every lesson in the draft with a full AI pass and can take a long time. Prefer generating per subtopic from the Module structure tab unless you really want to rebuild the whole plan.',
    );
    if (!ok) return;
    runAction('lessons', async () => {
      await generateLessons(course.id);
      await refreshDraft();
    });
  };

  const useOutlineAsModules = () => {
    const outlineTopics = parseOutlineModules(
      aiReferenceOutline || draft?.context?.aiReferenceOutline,
    );
    if (!outlineTopics.length) {
      window.alert('Add one module per line in the course outline first.');
      return;
    }
    const ok = window.confirm(
      topics.length || lessons.length
        ? 'This will replace current draft modules and sessions with the outline list. Continue?'
        : 'Create modules from the outline list?',
    );
    if (!ok) return;

    runAction('outlineModules', async () => {
      await persistDraft(
        {
          ...draft,
          context: {
            ...(draft?.context || {}),
            aiReferenceOutline,
          },
          structure: {
            ...(draft?.structure || {}),
            topics: outlineTopics,
          },
          lessons: {
            ...(draft?.lessons || {}),
            items: [],
            scheduleFit: computeScheduleFit(draft, []),
          },
          assets: {
            ...(draft?.assets || {}),
            courseCoverImage: {
              ...(draft?.assets?.courseCoverImage || {}),
              prompt: '',
              url: '',
            },
          },
          status: {
            ...(draft?.status || {}),
            steps: {
              ...(draft?.status?.steps || {}),
              structure: 'edited',
              lessons: 'not_started',
              images: 'not_started',
              assignments: 'not_started',
            },
          },
          generationJobs: {
            ...(draft?.generationJobs || {}),
            lessons: { status: 'idle' },
            images: {},
            assignments: { status: 'idle' },
            coverImage: { status: 'idle' },
          },
        },
        '',
      );
      await refreshDraft();
      setActiveTab('structure');
    });
  };

  const runGenerateModuleLessons = (topic) => {
    const subtopics = (topic?.subtopics || []).filter((subtopic) => subtopic?.id);
    if (!topic?.id || !subtopics.length) return;

    const existingCount = lessons.filter((lesson) =>
      subtopics.some((subtopic) => subtopic.id === lesson.subtopicId),
    ).length;
    if (
      existingCount &&
      !window.confirm(
        `This will rebuild ${existingCount} existing session${
          existingCount === 1 ? '' : 's'
        } linked to this module. Continue?`,
      )
    ) {
      return;
    }

    runAction(`topic-lessons-${topic.id}`, async () => {
      for (const subtopic of subtopics) {
        await generateLessonForSubtopic(course.id, topic.id, subtopic.id);
      }
      await refreshDraft();
      setActiveTab('lessons');
    });
  };

  const deleteLesson = (lessonId) => {
    const lesson = lessons.find((item) => item.id === lessonId);
    if (!lesson) return;
    if (
      !window.confirm(
        `Delete ${lesson.sessionLabel || lesson.title || 'this session'} from the draft?`,
      )
    ) {
      return;
    }

    runAction(`delete-lesson-${lessonId}`, async () => {
      const nextLessons = lessons.filter((item) => item.id !== lessonId);
      const nextImageJobs = { ...(draft?.generationJobs?.images || {}) };
      delete nextImageJobs[lessonId];

      await persistDraft(
        {
          ...draft,
          lessons: {
            ...(draft?.lessons || {}),
            items: nextLessons,
            scheduleFit: computeScheduleFit(draft, nextLessons),
          },
          generationJobs: {
            ...(draft?.generationJobs || {}),
            images: nextImageJobs,
          },
          status: {
            ...(draft?.status || {}),
            steps: {
              ...(draft?.status?.steps || {}),
              lessons: nextLessons.length ? draft?.status?.steps?.lessons || 'edited' : 'not_started',
              images: nextLessons.length ? draft?.status?.steps?.images || 'not_started' : 'not_started',
              assignments: nextLessons.length
                ? draft?.status?.steps?.assignments || 'not_started'
                : 'not_started',
            },
          },
        },
        'lessons',
      );
      await refreshDraft();
    });
  };

  return (
    <div className="grid grid-cols-1 gap-3.5 xl:grid-cols-[minmax(260px,300px)_minmax(0,1fr)] xl:gap-4 xl:items-start">
      <aside className="min-w-0 space-y-3 xl:sticky xl:top-4 xl:self-start">
        <PlannerSidebarReaderPanel
          courseId={course.id}
          lessons={lessons}
          navigate={navigate}
        />
        <CourseDetailsColumn
          key={courseId}
          course={course}
          courseId={courseId}
          draft={draft}
          busyAction={busyAction}
          runAction={runAction}
          refreshCourses={refreshCourses}
        />
      </aside>

      <div className="min-w-0 space-y-3.5">
      <div className="overflow-hidden rounded-xl border border-slate-200/90 bg-white shadow-sm ring-1 ring-slate-900/[0.04]">
        <div className="border-b border-slate-200/80 bg-slate-50/60 px-3 pt-2.5 sm:px-4">
          <nav className="-mb-px flex flex-wrap gap-1" aria-label="Planner sections">
            {PLANNER_TABS.map((tab) => {
              const needsDraft = tab.id !== 'overview';
              const disabled = needsDraft && !draft;
              return (
                <button
                  key={tab.id}
                  type="button"
                  onClick={() => selectTab(tab.id)}
                  disabled={disabled}
                  className={`rounded-t-lg px-2.5 py-2 text-sm font-medium transition sm:px-3.5 ${
                    activeTab === tab.id
                      ? 'border-b-2 border-fuchsia-600 bg-fuchsia-50/80 text-fuchsia-950'
                      : 'border-b-2 border-transparent text-slate-600 hover:bg-white hover:text-slate-900'
                  } ${disabled ? 'cursor-not-allowed opacity-40 hover:bg-transparent' : ''}`}
                >
                  {tab.label}
                </button>
              );
            })}
          </nav>
        </div>

        <div className="space-y-0 sm:space-y-4 sm:p-4">
          {activeTab === 'overview' && (
            <>
              <WorkshopSection
                eyebrow="Start here"
                title="Open your planner draft"
                subtitle="We save structure automatically after AI runs. You add sessions one subtopic at a time when you’re ready—no need to generate everything in one go."
                status={draftSteps.draft || 'not_started'}
              >
                <div className="flex flex-wrap items-center gap-3">
                  <Button onClick={handleCreateDraft} disabled={busyAction === 'createDraft'}>
                    {busyAction === 'createDraft' ? 'Opening…' : 'Create / open draft'}
                  </Button>
                  {draft?.context?.planning?.totalSessions ? (
                    <span className="text-sm text-gray-500">
                      Planned capacity: {draft.context.planning.totalSessions} sessions × 4 hours
                    </span>
                  ) : null}
                </div>
              </WorkshopSection>

              <WorkshopSection
                eyebrow="AI context"
                title="Course outline & reference for AI"
                subtitle="Paste a syllabus, unit list, NVQ criteria, theory/practical split, or any notes. The model reads this when you generate modules, bulk sessions, per-subtopic sessions, or regenerate a lesson."
                disabled={!draft}
              >
                {!draft ? (
                  <p className="text-sm text-gray-500">
                    Create or open a draft above, then add notes here and save.
                  </p>
                ) : (
                  <>
                    <label htmlFor="planner-ai-reference" className="sr-only">
                      Course outline and reference notes for AI
                    </label>
                    <textarea
                      id="planner-ai-reference"
                      className={textareaPlanner}
                      rows={10}
                      maxLength={12000}
                      placeholder="e.g. Week 1–2: consultation & skin analysis. Must cover patch tests. 60% practical. Link to City & Guilds unit 201…"
                      value={aiReferenceOutline}
                      onChange={(e) => setAiReferenceOutline(e.target.value.slice(0, 12000))}
                      disabled={busyAction === 'saveAiReference'}
                    />
                    <div className="mt-3 flex flex-wrap items-center gap-3">
                      <Button
                        type="button"
                        size="sm"
                        disabled={busyAction === 'saveAiReference'}
                        onClick={() =>
                          runAction('saveAiReference', async () => {
                            await persistDraft(
                              {
                                ...draft,
                                context: {
                                  ...(draft.context || {}),
                                  aiReferenceOutline,
                                },
                              },
                              '',
                            );
                          })
                        }
                      >
                        {busyAction === 'saveAiReference' ? 'Saving…' : 'Save outline to draft'}
                      </Button>
                      <span className="text-xs text-gray-500">
                        {aiReferenceOutline.length.toLocaleString()} / 12,000 (saved with this draft)
                      </span>
                    </div>
                  </>
                )}
              </WorkshopSection>
            </>
          )}

          {activeTab === 'structure' && (
            <WorkshopSection
              eyebrow="Curriculum map"
              title="Modules & subtopics"
              subtitle="Generate once, then tweak names. Use Build session on each row when you want that block written out—context stays with the subtopic."
              status={draftSteps.structure || 'not_started'}
              disabled={!draft}
              action={
                <div className="flex flex-wrap items-center gap-2">
                  <Button
                    size="sm"
                    onClick={() =>
                      runAction('structure', async () => {
                        await generateCourseStructure(course.id);
                        await refreshDraft();
                      })
                    }
                    disabled={!draft || busyAction === 'structure'}
                    className="gap-2"
                  >
                    <FaMagic className="text-xs" />
                    {busyAction === 'structure' ? 'Generating…' : topics.length ? 'Regenerate structure' : 'Generate structure'}
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={useOutlineAsModules}
                    disabled={
                      !draft ||
                      busyAction === 'outlineModules' ||
                      !(aiReferenceOutline || draft?.context?.aiReferenceOutline || '').trim()
                    }
                    className="gap-2"
                  >
                    <FaPlus className="text-xs" />
                    {busyAction === 'outlineModules' ? 'Creating…' : 'Use outline list'}
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={clearAllTopics}
                    disabled={!draft || busyAction === 'clearTopics' || (!topics.length && !lessons.length)}
                    className="gap-2 text-red-700"
                  >
                    <FaTrash className="text-xs" />
                    Delete all modules
                  </Button>
                </div>
              }
            >
              <JobStatusPanel
                title="Structure generation"
                job={generationJobs.structure}
              />
              <StructureEditor
                topics={topics}
                lessons={lessons}
                disabled={!draft}
                busyAction={busyAction}
                onAddTopic={(topic) =>
                  updateTopics([...topics, topic || createTopic(topics.length)])
                }
                onChange={updateTopics}
                onRegenerateTopic={(topicId) =>
                  runAction(`structure-${topicId}`, async () => {
                    await regenerateDraftItem(course.id, 'structure', topicId, 'replace');
                    await refreshDraft();
                  })
                }
                onGenerateLessonsForTopic={runGenerateModuleLessons}
                onGenerateLessonForSubtopic={(topicId, subtopicId) =>
                  runAction(`subtopic-lesson-${topicId}-${subtopicId}`, async () => {
                    await generateLessonForSubtopic(course.id, topicId, subtopicId);
                    await refreshDraft();
                  })
                }
              />
            </WorkshopSection>
          )}

          {activeTab === 'lessons' && (
            <WorkshopSection
              eyebrow="Session plans"
              title="What you’ll teach"
              subtitle="Open a row to edit detail. Regenerate pulls the linked module/subtopic so titles stay on-theme."
              status={draftSteps.lessons || 'not_started'}
              disabled={!draft || topics.length === 0}
            >
              <JobStatusPanel
                title="Lesson generation"
                job={generationJobs.lessons}
              />
              <ScheduleChips scheduleFit={scheduleFit} />
              <details className="mb-5 rounded-lg border border-amber-200/80 bg-amber-50/50 px-3 py-2.5 text-sm text-amber-950 sm:px-4 sm:py-3">
                <summary className="cursor-pointer font-medium text-amber-900">
                  Replace every session with AI (heavy)
                </summary>
                <p className="mt-2 text-amber-900/90">
                  Overwrites all drafted sessions. Prefer building from the Modules tab unless you
                  need a full reset.
                </p>
                <Button
                  size="sm"
                  variant="outline"
                  className="mt-3 border-amber-300 text-amber-900 hover:bg-amber-100"
                  onClick={runGenerateAllLessons}
                  disabled={!draft || topics.length === 0 || busyAction === 'lessons'}
                >
                  {busyAction === 'lessons' ? 'Generating…' : 'Rebuild all sessions'}
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  className="mt-3 ml-3 border-red-300 text-red-800 hover:bg-red-100"
                  onClick={clearAllLessons}
                  disabled={!draft || !lessons.length || busyAction === 'clearLessons'}
                >
                  Delete all sessions
                </Button>
              </details>
              <LessonsEditor
                courseId={course.id}
                lessons={lessons}
                disabled={!draft}
                onAddLesson={() => updateLessons([...lessons, createLesson(lessons.length)])}
                onChange={updateLessons}
                onDeleteLesson={deleteLesson}
                onRegenerateLesson={(lessonId) =>
                  runAction(`lesson-${lessonId}`, async () => {
                    await regenerateDraftItem(course.id, 'lesson', lessonId, 'replace');
                    await refreshDraft();
                  })
                }
                onGenerateLessonImage={(lessonId) =>
                  runAction(`image-${lessonId}`, async () => {
                    await generateLessonImage(course.id, lessonId);
                    await refreshDraft();
                  })
                }
              />
            </WorkshopSection>
          )}

          {activeTab === 'media' && (
            <>
              <WorkshopSection
                eyebrow="Visuals"
                title="Session images"
                status={draftSteps.images || 'not_started'}
                disabled={!draft || lessons.length === 0}
              >
                <JobStatusPanel
                  title="Cover image generation"
                  job={generationJobs.coverImage}
                />
                <LessonImagesEditor
                  lessons={lessons}
                  disabled={!draft || lessons.length === 0}
                  onChange={updateLessons}
                  imageJobs={generationJobs.images || {}}
                  onGenerate={(lessonId) =>
                    runAction(`image-${lessonId}`, async () => {
                      await generateLessonImage(course.id, lessonId);
                      await refreshDraft();
                    })
                  }
                  onRegenerate={(lessonId) =>
                    runAction(`regen-image-${lessonId}`, async () => {
                      await regenerateDraftItem(course.id, 'lessonImage', lessonId, 'replace');
                      await refreshDraft();
                    })
                  }
                  onUpload={async (lessonId, file) => {
                    await runAction(`upload-image-${lessonId}`, async () => {
                      const imageUrl = await uploadLessonPlannerAsset({
                        draftId: course.id,
                        file,
                        assetType: 'lesson-images',
                        itemId: lessonId,
                      });

                      await updateLessons(
                        lessons.map((lesson) =>
                          lesson.id === lessonId
                            ? {
                                ...lesson,
                                imageUrl,
                              }
                            : lesson,
                        ),
                        'images',
                      );
                    });
                  }}
                />
              </WorkshopSection>

              <div className="max-sm:mt-0 sm:mt-4">
                <WorkshopSection
                  eyebrow="Finishing touches"
                  title="Cover & assignments"
                  status={draftSteps.assignments || 'not_started'}
                  disabled={!draft || lessons.length === 0}
                  action={
                    <Button
                      size="sm"
                      onClick={() =>
                        runAction('assignments', async () => {
                          await generateAssignmentsAndCover(course.id);
                          await refreshDraft();
                        })
                      }
                      disabled={!draft || lessons.length === 0 || busyAction === 'assignments'}
                      className="gap-2"
                    >
                      <FaMagic className="text-xs" />
                      {busyAction === 'assignments'
                        ? 'Generating...'
                        : effectiveCourseCoverImage.url
                          ? 'Generate assignments'
                          : 'Generate assignments + cover'}
                    </Button>
                  }
                >
                  <JobStatusPanel
                    title="Assignments generation"
                    job={generationJobs.assignments}
                  />
                  <CoverImageEditor
                    courseName={course.name}
                    academicLevel={course.academicLevel}
                    coverImage={effectiveCourseCoverImage}
                    disabled={!draft || lessons.length === 0}
                    onChange={updateCoverImage}
                    onRegenerate={() =>
                      runAction('coverImage', async () => {
                        if (effectiveCourseCoverImage.url) {
                          const ok = window.confirm(
                            'Replace the current course cover with a newly generated image?',
                          );
                          if (!ok) return;
                        }
                        await regenerateDraftItem(course.id, 'coverImage', '', 'replace');
                        await refreshDraft();
                      })
                    }
                    onUpload={async (file) => {
                      await runAction('coverUpload', async () => {
                        const url = await uploadLessonPlannerAsset({
                          draftId: course.id,
                          file,
                          assetType: 'course-cover',
                        });
                        await updateCoverImage({ url, source: 'uploaded' });
                      });
                    }}
                  />
                  <AssignmentsEditor
                    lessons={lessons}
                    disabled={!draft || lessons.length === 0}
                    onChange={updateLessons}
                    onRegenerate={(lessonId) =>
                      runAction(`assignment-${lessonId}`, async () => {
                        await regenerateDraftItem(course.id, 'assignment', lessonId, 'replace');
                        await refreshDraft();
                      })
                    }
                  />
                </WorkshopSection>
              </div>
            </>
          )}

        </div>
      </div>
      </div>
    </div>
  );
};

const CourseDetailsColumn = ({
  course,
  courseId,
  draft,
  busyAction,
  runAction,
  refreshCourses,
}) => {
  const [form, setForm] = useState(() => buildCourseFormState(course));

  const cap = draft?.context?.planning?.totalSessions;
  const saveKey = 'saveCourseDetails';

  const handleSave = () =>
    runAction(saveKey, async () => {
      const months = form.isOneDay
        ? 0
        : Math.max(1, Math.min(120, Number(form.durationMonths) || 1));
      const payload = {
        name: form.name.trim() || 'Untitled course',
        courseCode: form.courseCode.trim(),
        type: form.type,
        academicLevel: form.academicLevel,
        description: form.description.trim(),
        status: form.status,
        duration: {
          months,
          isOneDay: Boolean(form.isOneDay),
        },
        planningCoverage: {
          totalHours: parseOptionalHours(form.coverageTotal),
          theoryHours: parseOptionalHours(form.coverageTheory),
          practicalHours: parseOptionalHours(form.coveragePractical),
        },
      };
      await update(ref(db, `courses/${courseId}`), payload);
      const snap = await get(ref(db, `courses/${courseId}`));
      setForm(buildCourseFormState(snap.val() || {}));
      await refreshCourses();
    });

  const patch = (updates) => setForm((prev) => ({ ...prev, ...updates }));

  return (
    <section className="rounded-lg border border-gray-200 bg-white p-3 shadow-sm sm:p-5">
      <div className="flex items-start justify-between gap-2">
        <p className="text-xs font-semibold uppercase tracking-wide text-pink-700">Course details</p>
        <span
          className={`shrink-0 rounded-full px-2 py-0.5 text-[10px] font-medium capitalize ${getCourseStatusClass(
            course.status || 'draft',
          )}`}
        >
          {course.status || 'draft'}
        </span>
      </div>
      <p className="mt-1 text-xs text-gray-500">
        Edits save to the live course. Use full course settings for fees, batches, and media.
      </p>

      <div className="mt-4 space-y-3">
        <div>
          <label className="mb-1 block text-xs font-medium text-gray-700">Course name</label>
          <input
            type="text"
            value={form.name}
            onChange={(e) => patch({ name: e.target.value })}
            className={inputPlanner}
          />
        </div>
        <div>
          <label className="mb-1 block text-xs font-medium text-gray-700">Course code</label>
          <input
            type="text"
            value={form.courseCode}
            onChange={(e) => patch({ courseCode: e.target.value })}
            className={inputPlanner}
          />
        </div>
        <div>
          <label className="mb-1 block text-xs font-medium text-gray-700">Type</label>
          <select
            value={form.type}
            onChange={(e) => patch({ type: e.target.value })}
            className={inputPlanner}
          >
            <option value="physical">Physical</option>
            <option value="online">Online</option>
            <option value="hybrid">Hybrid</option>
          </select>
        </div>
        <div>
          <label className="mb-1 block text-xs font-medium text-gray-700">Academic level</label>
          <select
            value={form.academicLevel}
            onChange={(e) => patch({ academicLevel: e.target.value })}
            className={inputPlanner}
          >
            <option value="certificate">Certificate</option>
            <option value="diploma">Diploma</option>
            <option value="nvq3">NVQ Level 3</option>
            <option value="nvq4">NVQ Level 4</option>
          </select>
        </div>
        <div>
          <label className="mb-1 block text-xs font-medium text-gray-700">Status</label>
          <select
            value={form.status}
            onChange={(e) => patch({ status: e.target.value })}
            className={inputPlanner}
          >
            <option value="draft">Draft</option>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
          </select>
        </div>
        <div>
          <label className="mb-1 block text-xs font-medium text-gray-700">Description</label>
          <textarea
            value={form.description}
            onChange={(e) => patch({ description: e.target.value })}
            rows={4}
            className={textareaPlanner}
            placeholder="Shown on course pages and helps the AI planner."
          />
        </div>

        <div className="rounded-md border border-gray-100 bg-gray-50/80 p-3">
          <p className="text-xs font-semibold text-gray-800">Duration (planner)</p>
          <div className="mt-2 flex items-center gap-2">
            <input
              type="checkbox"
              id="planner-is-one-day"
              checked={form.isOneDay}
              onChange={(e) =>
                patch({
                  isOneDay: e.target.checked,
                  durationMonths: e.target.checked ? 0 : Math.max(1, form.durationMonths || 1),
                })
              }
              className="h-4 w-4 rounded border-gray-300 text-pink-600 focus:ring-pink-500"
            />
            <label htmlFor="planner-is-one-day" className="text-sm text-gray-700">
              One-day workshop
            </label>
          </div>
          {!form.isOneDay ? (
            <div className="mt-2">
              <label className="mb-1 block text-xs font-medium text-gray-600">Months</label>
              <input
                type="number"
                min={1}
                max={120}
                value={form.durationMonths}
                onChange={(e) => patch({ durationMonths: Number(e.target.value) || 1 })}
                className={inputPlanner}
              />
            </div>
          ) : null}
        </div>

        <div className="rounded-md border border-dashed border-gray-200 bg-white p-3">
          <p className="text-xs font-semibold text-gray-800">Hours to cover (optional)</p>
          <p className="mt-0.5 text-[11px] text-gray-500">
            Guide for planning; does not change session math until you adjust course duration above.
          </p>
          <div className="mt-2 space-y-2">
            <div>
              <label className="mb-1 block text-xs text-gray-600">Total contact hours</label>
              <input
                type="number"
                min={0}
                step={0.5}
                value={form.coverageTotal}
                onChange={(e) => patch({ coverageTotal: e.target.value })}
                className={inputPlanner}
                placeholder="e.g. 120"
              />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="mb-1 block text-xs text-gray-600">Theory (h)</label>
                <input
                  type="number"
                  min={0}
                  step={0.5}
                  value={form.coverageTheory}
                  onChange={(e) => patch({ coverageTheory: e.target.value })}
                  className={inputPlanner}
                  placeholder="—"
                />
              </div>
              <div>
                <label className="mb-1 block text-xs text-gray-600">Practical (h)</label>
                <input
                  type="number"
                  min={0}
                  step={0.5}
                  value={form.coveragePractical}
                  onChange={(e) => patch({ coveragePractical: e.target.value })}
                  className={inputPlanner}
                  placeholder="—"
                />
              </div>
            </div>
          </div>
        </div>

        <Button
          type="button"
          size="sm"
          className="w-full"
          disabled={busyAction === saveKey}
          onClick={handleSave}
        >
          {busyAction === saveKey ? 'Saving…' : 'Save course details'}
        </Button>

        <dl className="border-t border-gray-100 pt-3 text-xs text-gray-600">
          <div className="flex justify-between gap-2 py-0.5">
            <dt className="text-gray-500">Live lessons</dt>
            <dd className="font-medium text-gray-800">{course.lessons?.length ?? 0}</dd>
          </div>
          <div className="flex justify-between gap-2 py-0.5">
            <dt className="text-gray-500">Students</dt>
            <dd className="font-medium text-gray-800">{course.totalStudents ?? 0}</dd>
          </div>
          {cap ? (
            <div className="flex justify-between gap-2 py-0.5">
              <dt className="text-gray-500">AI session cap</dt>
              <dd className="font-medium text-gray-800">{cap} × 4h</dd>
            </div>
          ) : null}
          {course.planningCoverage?.totalHours != null ? (
            <div className="py-1">
              <dt className="text-gray-500">Coverage target</dt>
              <dd className="mt-0.5 font-medium text-gray-800">
                {course.planningCoverage.totalHours}h total
                {(course.planningCoverage.theoryHours != null ||
                  course.planningCoverage.practicalHours != null) && (
                  <span className="mt-0.5 block text-[10px] font-normal text-gray-500">
                    {[
                      course.planningCoverage.theoryHours != null &&
                        `Theory ${course.planningCoverage.theoryHours}h`,
                      course.planningCoverage.practicalHours != null &&
                        `Practical ${course.planningCoverage.practicalHours}h`,
                    ]
                      .filter(Boolean)
                      .join(' · ')}
                  </span>
                )}
              </dd>
            </div>
          ) : null}
        </dl>
      </div>
    </section>
  );
};

const WorkshopSection = ({
  eyebrow,
  title,
  subtitle,
  status,
  action,
  disabled = false,
  children,
}) => (
  <section
    className={[
      // Mobile: same horizontal rhythm as tabs (px-3), flat sections with dividers — max width for content
      'bg-white px-3 py-3.5 max-sm:border-b max-sm:border-gray-200 max-sm:last:border-b-0 sm:rounded-lg sm:border sm:border-gray-200 sm:bg-gray-50/50 sm:p-4',
      disabled ? 'pointer-events-none opacity-50' : '',
    ]
      .filter(Boolean)
      .join(' ')}
  >
    <header className="flex flex-col gap-2 border-b border-gray-200 pb-2.5 sm:flex-row sm:items-start sm:justify-between sm:gap-3 sm:pb-3">
      <div className="min-w-0">
        {eyebrow ? (
          <p className="text-xs font-semibold uppercase tracking-wide text-pink-700">{eyebrow}</p>
        ) : null}
        <h2 className="mt-0.5 text-base font-semibold text-gray-900 sm:text-lg">{title}</h2>
        {subtitle ? (
          <p className="mt-1 max-w-none text-sm leading-6 text-gray-600 sm:max-w-3xl">
            {subtitle}
          </p>
        ) : null}
      </div>
      <div className="flex shrink-0 flex-wrap items-center gap-2">
        {status !== undefined && status !== null ? (
          <span
            className={`rounded-full px-2.5 py-0.5 text-xs font-medium capitalize ${getStepStatusClass(
              status,
            )}`}
          >
            {getStepStatusLabel(status)}
          </span>
        ) : null}
        {action}
      </div>
    </header>
    <div className="pt-3">{children}</div>
  </section>
);

const JobStatusPanel = ({ job, title }) => {
  if (!job) return null;

  return (
    <div className="mb-3 rounded-lg border border-slate-200 bg-white p-2.5 text-sm shadow-sm">
      <div className="flex flex-wrap items-center gap-2">
        <span className="font-medium text-slate-900">{title}</span>
        <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${getStepStatusClass(job.status)}`}>
          {getStepStatusLabel(job.status)}
        </span>
        {job.provider ? (
          <span className="rounded-full bg-slate-100 px-2 py-0.5 text-xs text-slate-700">
            {job.provider}
          </span>
        ) : null}
        {job.model ? (
          <span className="rounded-full bg-slate-100 px-2 py-0.5 text-xs text-slate-700">
            {job.model}
          </span>
        ) : null}
        {job.promptVersion ? (
          <span className="rounded-full bg-slate-100 px-2 py-0.5 text-xs text-slate-700">
            {job.promptVersion}
          </span>
        ) : null}
      </div>
      {job.error ? (
        <p className="mt-2 rounded-md bg-red-50 px-3 py-2 text-xs text-red-700">
          {job.error}
        </p>
      ) : null}
    </div>
  );
};

const ScheduleChips = ({ scheduleFit }) => {
  const total = scheduleFit.totalSessions || 0;
  const drafted = scheduleFit.generatedLessons || 0;
  const overflow = scheduleFit.overflowCount || 0;
  const last = scheduleFit.lastPlannedLessonDate;

  return (
    <div className="mb-3 flex flex-wrap items-center gap-1.5 text-sm">
      <span className="rounded-full bg-gray-200/80 px-3 py-1 font-medium text-gray-800">
        {total} slots in plan
      </span>
      <span className="rounded-full bg-pink-100 px-3 py-1 font-medium text-pink-950">
        {drafted} drafted
      </span>
      {overflow > 0 ? (
        <span className="rounded-full bg-amber-100 px-3 py-1 font-medium text-amber-900">
          {overflow} over cap
        </span>
      ) : null}
      {last ? (
        <span className="rounded-full bg-white px-3 py-1 text-gray-600 ring-1 ring-gray-200">
          Last: {last}
        </span>
      ) : null}
    </div>
  );
};

const StructureEditor = ({
  topics,
  lessons,
  disabled,
  busyAction,
  onAddTopic,
  onChange,
  onRegenerateTopic,
  onGenerateLessonsForTopic,
  onGenerateLessonForSubtopic,
}) => {
  const [openTopicId, setOpenTopicId] = useState(null);
  const topicRefs = useRef({});
  const pendingScrollTopicIdRef = useRef(null);

  useEffect(() => {
    if (!topics.length) {
      setOpenTopicId(null);
      return;
    }
    setOpenTopicId((prev) => {
      if (prev && topics.some((t) => t.id === prev)) return prev;
      return topics[0].id;
    });
  }, [topics]);

  useEffect(() => {
    const topicId = pendingScrollTopicIdRef.current;
    if (!topicId || openTopicId !== topicId) return;

    window.requestAnimationFrame(() => {
      topicRefs.current[topicId]?.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });
      pendingScrollTopicIdRef.current = null;
    });
  }, [openTopicId, topics]);

  const addTopic = () => {
    const nextTopic = createTopic(topics.length);
    pendingScrollTopicIdRef.current = nextTopic.id;
    setOpenTopicId(nextTopic.id);
    onAddTopic(nextTopic);
  };

  const updateTopic = (index, patch) => {
    onChange(
      topics.map((topic, topicIndex) =>
        topicIndex === index ? { ...topic, ...patch } : topic,
      ),
    );
  };

  const updateSubtopic = (topicIndex, subtopicIndex, patch) => {
    onChange(
      topics.map((topic, currentTopicIndex) =>
        currentTopicIndex === topicIndex
          ? {
              ...topic,
              subtopics: (topic.subtopics || []).map((subtopic, currentSubtopicIndex) =>
                currentSubtopicIndex === subtopicIndex
                  ? { ...subtopic, ...patch }
                  : subtopic,
              ),
            }
          : topic,
      ),
    );
  };

  const addSubtopic = (topicIndex) => {
    onChange(
      topics.map((topic, currentTopicIndex) =>
        currentTopicIndex === topicIndex
          ? {
              ...topic,
              subtopics: [
                ...(topic.subtopics || []),
                {
                  id: `subtopic-manual-${Date.now()}-${topicIndex}`,
                  title: '',
                  description: '',
                },
              ],
            }
          : topic,
      ),
    );
  };

  const removeSubtopic = (topicIndex, subtopicIndex) => {
    const topic = topics[topicIndex];
    const subs = topic?.subtopics || [];
    if (subs.length <= 1) return;
    onChange(
      topics.map((t, i) =>
        i === topicIndex
          ? {
              ...t,
              subtopics: subs.filter((_, j) => j !== subtopicIndex),
            }
          : t,
      ),
    );
  };

  const removeTopic = (topicIndex) => {
    if (topics.length <= 1) return;
    if (!window.confirm('Remove this topic and all its subtopics?')) return;
    onChange(topics.filter((_, i) => i !== topicIndex));
  };

  return (
    <div className="space-y-3">
      <div className="flex justify-end">
        <Button
          variant="outline"
          size="sm"
          onClick={addTopic}
          disabled={disabled}
          className="gap-2"
        >
          <FaPlus className="text-xs" />
          Add module
        </Button>
      </div>

      {topics.length === 0 ? (
        <EmptyState message="Run AI outline above, or add a module by hand." />
      ) : (
        topics.map((topic, topicIndex) => {
          const topicKey = topic.id;
          const isOpen = openTopicId === topicKey;
          const subCount = (topic.subtopics || []).length;
          const topicBusyKey = `topic-lessons-${topic.id}`;
          const topicBusy = busyAction === topicBusyKey;

          return (
            <div
              key={topicKey || topicIndex}
              ref={(node) => {
                if (node) {
                  topicRefs.current[topicKey] = node;
                } else {
                  delete topicRefs.current[topicKey];
                }
              }}
              className="overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm"
            >
              <button
                type="button"
                disabled={disabled}
                onClick={() => setOpenTopicId(isOpen ? null : topicKey)}
                className="flex w-full items-center gap-3 px-3 py-3 text-left transition hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50 sm:px-4"
              >
                <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-fuchsia-600 text-xs font-bold text-white shadow-sm">
                  {topicIndex + 1}
                </span>
                <div className="min-w-0 flex-1">
                  <p className="truncate font-medium text-gray-900">
                    {topic.title || `Module ${topicIndex + 1}`}
                  </p>
                  <p className="text-xs text-gray-500">{subCount} subtopic{subCount === 1 ? '' : 's'}</p>
                </div>
                <FaAngleDown
                  className={`h-4 w-4 shrink-0 text-gray-400 transition-transform ${isOpen ? 'rotate-180' : ''}`}
                  aria-hidden
                />
              </button>

              {isOpen ? (
                <div className="space-y-4 border-t border-gray-100 px-3 pb-3 pt-3 sm:px-4 sm:pb-4">
                  <div className="flex flex-wrap items-center gap-2">
                    <Button
                      size="sm"
                      onClick={() => onGenerateLessonsForTopic(topic)}
                      disabled={disabled || !subCount || topicBusy}
                      className="gap-1.5 bg-fuchsia-700 px-3 text-xs text-white shadow-sm hover:bg-fuchsia-800"
                    >
                      <FaMagic className="text-[10px]" />
                      {topicBusy ? 'Building module...' : 'Build module sessions'}
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => onRegenerateTopic(topic.id)}
                      disabled={disabled}
                      className="gap-1.5 text-xs"
                    >
                      <FaRedo className="text-[10px]" />
                      Refresh module (AI)
                    </Button>
                    {topics.length > 1 ? (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => removeTopic(topicIndex)}
                        disabled={disabled}
                        className="gap-1.5 border-red-200 text-xs text-red-700 hover:bg-red-50"
                      >
                        <FaTrash className="text-[10px]" />
                        Remove
                      </Button>
                    ) : null}
                  </div>

                  <div className="grid gap-3">
                    <EditableField
                      label="Module title"
                      value={topic.title || ''}
                      onChange={(value) => updateTopic(topicIndex, { title: value })}
                    />
                    <EditableArea
                      label="What this block covers"
                      value={topic.description || ''}
                      onChange={(value) => updateTopic(topicIndex, { description: value })}
                      rows={2}
                    />
                  </div>

                  <div>
                    <div className="mb-2 flex items-center justify-between gap-2">
                      <p className="text-sm font-medium text-gray-700">Subtopics</p>
                      <button
                        type="button"
                        onClick={() => addSubtopic(topicIndex)}
                        disabled={disabled}
                        className="inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs font-medium text-fuchsia-800 hover:bg-fuchsia-50 disabled:opacity-40"
                      >
                        <FaPlus className="text-[10px]" />
                        Add
                      </button>
                    </div>
                    <ul className="space-y-2">
                      {(topic.subtopics || []).map((subtopic, subtopicIndex) => {
                        const linkedLesson = (lessons || []).find(
                          (l) => l.subtopicId === subtopic.id,
                        );
                        const subBusyKey = `subtopic-lesson-${topic.id}-${subtopic.id}`;
                        const subBusy = busyAction === subBusyKey;
                        return (
                          <li
                            key={subtopic.id || subtopicIndex}
                            className="rounded-lg border border-gray-200 bg-gray-50/50 p-2.5 sm:p-3"
                          >
                            <div className="flex flex-col gap-3 lg:flex-row lg:items-end">
                              <div className="min-w-0 flex-1 space-y-2">
                                <div className="flex items-center gap-2">
                                  <input
                                    type="text"
                                    value={subtopic.title || ''}
                                    onChange={(e) =>
                                      updateSubtopic(topicIndex, subtopicIndex, {
                                        title: e.target.value,
                                      })
                                    }
                                    disabled={disabled}
                                    className={inputPlanner}
                                    placeholder="Subtopic name (specific skill or theme)"
                                  />
                                  {linkedLesson ? (
                                    <span className="shrink-0 rounded-full bg-emerald-100 px-2 py-0.5 text-[10px] font-semibold text-emerald-800">
                                      Session
                                    </span>
                                  ) : null}
                                </div>
                                <textarea
                                  value={subtopic.description || ''}
                                  onChange={(e) =>
                                    updateSubtopic(topicIndex, subtopicIndex, {
                                      description: e.target.value,
                                    })
                                  }
                                  disabled={disabled}
                                  rows={2}
                                  className={textareaPlanner}
                                  placeholder="Short note on outcomes for this slice"
                                />
                              </div>
                              <div className="flex shrink-0 flex-col gap-2 sm:flex-row lg:flex-col">
                                <Button
                                  type="button"
                                  size="sm"
                                  variant={linkedLesson ? 'outline' : undefined}
                                  disabled={disabled || subBusy}
                                  className={`gap-1.5 text-xs ${
                                    linkedLesson
                                      ? 'border-gray-200 text-gray-500 hover:bg-gray-50'
                                      : ''
                                  }`}
                                  onClick={() =>
                                    onGenerateLessonForSubtopic(topic.id, subtopic.id)
                                  }
                                >
                                  <FaMagic className="text-[10px]" />
                                  {subBusy
                                    ? 'Working…'
                                    : linkedLesson
                                      ? 'Rebuild session'
                                      : 'Build session'}
                                </Button>
                                <button
                                  type="button"
                                  title={
                                    (topic.subtopics || []).length <= 1
                                      ? 'Keep at least one subtopic'
                                      : 'Remove subtopic'
                                  }
                                  disabled={disabled || (topic.subtopics || []).length <= 1}
                                  onClick={() => removeSubtopic(topicIndex, subtopicIndex)}
                                  className="rounded-md border border-gray-200 py-2 text-xs text-gray-500 hover:border-red-200 hover:bg-red-50 hover:text-red-700 disabled:opacity-40"
                                >
                                  Remove
                                </button>
                              </div>
                            </div>
                          </li>
                        );
                      })}
                    </ul>
                  </div>
                </div>
              ) : null}
            </div>
          );
        })
      )}
    </div>
  );
};

const LessonsEditor = ({
  courseId,
  lessons,
  disabled,
  onAddLesson,
  onChange,
  onDeleteLesson,
  onRegenerateLesson,
  onGenerateLessonImage,
}) => {
  const [openLessonId, setOpenLessonId] = useState(null);
  const [attachLinkDraft, setAttachLinkDraft] = useState({ title: '', url: '' });
  const [uploadBusyLessonId, setUploadBusyLessonId] = useState(null);
  const [videoDraftRowsByLesson, setVideoDraftRowsByLesson] = useState({});

  useEffect(() => {
    setAttachLinkDraft({ title: '', url: '' });
  }, [openLessonId]);

  useEffect(() => {
    if (!lessons.length) {
      setOpenLessonId(null);
      return;
    }
    setOpenLessonId((prev) => {
      if (prev && lessons.some((l) => l.id === prev)) return prev;
      return null;
    });
  }, [lessons]);

  useEffect(() => {
    const lessonIds = new Set(lessons.map((lesson) => lesson.id).filter(Boolean));
    setVideoDraftRowsByLesson((prev) =>
      Object.fromEntries(
        Object.entries(prev).filter(([lessonId]) => lessonIds.has(lessonId)),
      ),
    );
  }, [lessons]);

  const updateLesson = (lessonId, patch) => {
    onChange(
      lessons.map((lesson) =>
        lesson.id === lessonId ? { ...lesson, ...patch } : lesson,
      ),
    );
  };

  const updateLessonContent = (lessonId, contentPatch) => {
    onChange(
      lessons.map((lesson) =>
        lesson.id === lessonId
          ? syncLessonWithContent(lesson, contentPatch)
          : lesson,
      ),
    );
  };

  const updateLessonVideos = (lessonId, videos) => {
    const cleaned = videos.map((item) => item.trim());
    const saved = cleaned.filter(Boolean);
    setVideoDraftRowsByLesson((prev) => ({
      ...prev,
      [lessonId]: cleaned.length ? cleaned : [''],
    }));
    updateLesson(lessonId, {
      lessonVideos: saved,
      videoUrl: saved[0] || '',
    });
  };

  return (
    <div className="space-y-3">
      <div className="flex justify-end">
        <Button
          variant="outline"
          size="sm"
          onClick={onAddLesson}
          disabled={disabled}
          className="gap-2"
        >
          <FaPlus className="text-xs" />
          Add session
        </Button>
      </div>

      {lessons.length === 0 ? (
        <EmptyState message="Build sessions from the Modules tab, or add one manually." />
      ) : (
        <ul className="space-y-2">
          {lessons.map((lesson, index) => {
            const isOpen = openLessonId === lesson.id;
            return (
              <li
                key={lesson.id || index}
                className="overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm"
              >
                <div className="flex items-stretch gap-0">
                  <button
                    type="button"
                    disabled={disabled}
                    onClick={() => setOpenLessonId(isOpen ? null : lesson.id)}
                    className="flex min-w-0 flex-1 items-center gap-3 px-3 py-3 text-left transition hover:bg-gray-50 disabled:opacity-50 sm:px-4"
                  >
                    <span className="shrink-0 text-xs font-semibold text-gray-400">
                      {lesson.sessionLabel || `S${index + 1}`}
                    </span>
                    <div className="min-w-0 flex-1">
                      <p className="truncate font-medium text-gray-900">
                        {lesson.title || 'Untitled session'}
                      </p>
                      <p className="text-xs text-gray-500">
                        {(lesson.estimatedMinutes || 240) / 60}h ·{' '}
                        {lesson.sessionType || 'blended'}
                        {lesson.subtopicId ? (
                          <span className="text-emerald-700"> · linked</span>
                        ) : null}
                      </p>
                    </div>
                    <FaAngleDown
                      className={`h-4 w-4 shrink-0 text-gray-400 transition-transform ${isOpen ? 'rotate-180' : ''}`}
                    />
                  </button>
                  <div className="flex shrink-0 items-center gap-1 border-l border-gray-100 px-2">
                    {typeof onGenerateLessonImage === 'function' ? (
                      <button
                        type="button"
                        disabled={disabled}
                        title="Generate image"
                        onClick={(e) => {
                          e.stopPropagation();
                          onGenerateLessonImage(lesson.id);
                        }}
                        className="rounded-lg p-2 text-fuchsia-800 hover:bg-fuchsia-50 disabled:opacity-40"
                      >
                        <FaMagic className="text-sm" />
                      </button>
                    ) : null}
                    <button
                      type="button"
                      disabled={disabled}
                      title="Regenerate session"
                      onClick={(e) => {
                        e.stopPropagation();
                        onRegenerateLesson(lesson.id);
                      }}
                      className="rounded-lg p-2 text-gray-500 hover:bg-gray-100 disabled:opacity-40"
                    >
                      <FaRedo className="text-sm" />
                    </button>
                    <button
                      type="button"
                      disabled={disabled}
                      title="Delete session"
                      onClick={(e) => {
                        e.stopPropagation();
                        onDeleteLesson?.(lesson.id);
                      }}
                      className="rounded-lg p-2 text-red-600 hover:bg-red-50 disabled:opacity-40"
                    >
                      <FaTrash className="text-sm" />
                    </button>
                  </div>
                </div>

                {isOpen ? (
                  <div className="space-y-3 border-t border-gray-100 px-3 pb-3 pt-3 sm:px-4 sm:pb-4">
                    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                      <EditableField
                        label="Session label"
                        value={lesson.sessionLabel || ''}
                        onChange={(value) => updateLesson(lesson.id, { sessionLabel: value })}
                      />
                      <EditableField
                        label="Session #"
                        type="number"
                        value={lesson.sessionNumber || index + 1}
                        onChange={(value) =>
                          updateLesson(lesson.id, {
                            sessionNumber: Number(value) || index + 1,
                          })
                        }
                      />
                      <EditableField
                        label="Session title"
                        value={lesson.title || ''}
                        onChange={(value) => updateLesson(lesson.id, { title: value })}
                      />
                      <div>
                        <label className="mb-1.5 block text-sm font-medium text-gray-700">
                          Format
                        </label>
                        <select
                          value={lesson.sessionType || 'blended'}
                          onChange={(e) =>
                            updateLesson(lesson.id, { sessionType: e.target.value })
                          }
                          className={inputPlanner}
                        >
                          <option value="blended">Blended</option>
                          <option value="practical">Practical</option>
                          <option value="theory">Theory</option>
                          <option value="assessment">Assessment</option>
                        </select>
                      </div>
                      <EditableField
                        label="Minutes"
                        type="number"
                        value={lesson.estimatedMinutes || 0}
                        onChange={(value) =>
                          updateLesson(lesson.id, { estimatedMinutes: Number(value) || 0 })
                        }
                      />
                    </div>

                    <EditableArea
                      label="Session overview"
                      value={normalizeLessonContent(lesson.content).overview}
                      onChange={(value) =>
                        updateLessonContent(lesson.id, { overview: value })
                      }
                    />

                    <EditableArea
                      label="Learning outcomes (one per line)"
                      value={normalizeLessonContent(lesson.content).learningOutcomes.join('\n')}
                      onChange={(value) =>
                        updateLessonContent(lesson.id, {
                          learningOutcomes: value
                            .split('\n')
                            .map((item) => item.trim())
                            .filter(Boolean),
                        })
                      }
                    />

                    <EditableArea
                      label="Lesson flow (one per line)"
                      value={normalizeLessonContent(lesson.content).lessonFlow.join('\n')}
                      onChange={(value) =>
                        updateLessonContent(lesson.id, {
                          lessonFlow: value
                            .split('\n')
                            .map((item) => item.trim())
                            .filter(Boolean),
                        })
                      }
                    />

                    <EditableArea
                      label="Materials (one per line)"
                      value={normalizeLessonContent(lesson.content).materials.join('\n')}
                      onChange={(value) =>
                        updateLessonContent(lesson.id, {
                          materials: value
                            .split('\n')
                            .map((item) => item.trim())
                            .filter(Boolean),
                        })
                      }
                    />

                    <EditableArea
                      label="Activities (one per line)"
                      value={normalizeLessonContent(lesson.content).activities.join('\n')}
                      onChange={(value) =>
                        updateLessonContent(lesson.id, {
                          activities: value
                            .split('\n')
                            .map((item) => item.trim())
                            .filter(Boolean),
                        })
                      }
                    />

                    <EditableArea
                      label="Safety & hygiene (one per line)"
                      value={normalizeLessonContent(lesson.content).safetyAndHygiene.join('\n')}
                      onChange={(value) =>
                        updateLessonContent(lesson.id, {
                          safetyAndHygiene: value
                            .split('\n')
                            .map((item) => item.trim())
                            .filter(Boolean),
                        })
                      }
                    />

                    <EditableArea
                      label="Assessment"
                      value={normalizeLessonContent(lesson.content).assessment}
                      onChange={(value) =>
                        updateLessonContent(lesson.id, { assessment: value })
                      }
                    />

                    <EditableArea
                      label="Home practice"
                      value={normalizeLessonContent(lesson.content).homePractice}
                      onChange={(value) =>
                        updateLessonContent(lesson.id, { homePractice: value })
                      }
                    />

                    <EditableArea
                      label="Instructor notes"
                      value={normalizeLessonContent(lesson.content).instructorNotes}
                      onChange={(value) =>
                        updateLessonContent(lesson.id, { instructorNotes: value })
                      }
                    />

                    <EditableArea
                      label="Readings (one per line)"
                      value={(lesson.extraReadings || []).join('\n')}
                      onChange={(value) =>
                        updateLesson(lesson.id, {
                          extraReadings: value
                            .split('\n')
                            .map((item) => item.trim())
                            .filter(Boolean),
                        })
                      }
                    />

                    <div className="rounded-lg border border-slate-200 bg-slate-50/60 p-3">
                      <p className="mb-2 flex items-center gap-2 text-sm font-medium text-gray-800">
                        <FaYoutube className="text-red-600" />
                        Lesson videos
                      </p>
                      <div className="space-y-2">
                        {(() => {
                          const videos =
                            videoDraftRowsByLesson[lesson.id] ||
                            normalizeLessonVideos(lesson);
                          const videoRows = videos.length ? videos : [''];
                          return videoRows.map((videoUrl, videoIndex) => (
                            <div
                              key={`${lesson.id}-video-${videoIndex}`}
                              className="flex flex-col gap-2 sm:flex-row"
                            >
                              <input
                                type="url"
                                disabled={disabled}
                                className={inputPlanner}
                                value={videoUrl}
                                onChange={(e) => {
                                  const nextVideos = [...videoRows];
                                  nextVideos[videoIndex] = e.target.value;
                                  updateLessonVideos(lesson.id, nextVideos);
                                }}
                                placeholder="https://www.youtube.com/watch?v=..."
                              />
                              <button
                                type="button"
                                disabled={disabled || (!videoUrl && videoRows.length === 1)}
                                onClick={() => {
                                  const nextVideos = videoRows.filter(
                                    (_, indexToKeep) => indexToKeep !== videoIndex,
                                  );
                                  updateLessonVideos(lesson.id, nextVideos);
                                }}
                                className="rounded-md border border-gray-200 px-3 py-2 text-xs text-gray-500 hover:border-red-200 hover:bg-red-50 hover:text-red-700 disabled:opacity-40"
                              >
                                Remove
                              </button>
                            </div>
                          ));
                        })()}
                        <Button
                          type="button"
                          size="sm"
                          variant="outline"
                          disabled={disabled}
                          className="gap-1.5 text-xs"
                          onClick={() =>
                            updateLessonVideos(lesson.id, [
                              ...(videoDraftRowsByLesson[lesson.id] ||
                                normalizeLessonVideos(lesson)),
                              '',
                            ])
                          }
                        >
                          <FaPlus className="text-[10px]" />
                          Add video
                        </Button>
                      </div>
                      <div className="mt-4 border-t border-slate-200/80 pt-4">
                      <p className="mb-2 flex items-center gap-2 text-sm font-medium text-gray-800">
                        <FaPaperclip className="text-gray-600" />
                        Attachments (files or links)
                      </p>
                      <ul className="mb-3 space-y-2">
                        {(Array.isArray(lesson.attachments) ? lesson.attachments : []).map(
                          (att) =>
                            att?.url ? (
                              <li
                                key={att.id || att.url}
                                className="flex items-start justify-between gap-2 rounded-md border border-white bg-white/90 px-2 py-1.5 text-xs"
                              >
                                <div className="min-w-0">
                                  <p className="truncate font-medium text-gray-800">
                                    {att.title || 'Document'}
                                  </p>
                                  <p className="truncate text-gray-500">{att.url}</p>
                                </div>
                                <button
                                  type="button"
                                  disabled={disabled}
                                  title="Remove"
                                  onClick={() =>
                                    updateLesson(lesson.id, {
                                      attachments: (lesson.attachments || []).filter(
                                        (a) => (a.id || a.url) !== (att.id || att.url),
                                      ),
                                    })
                                  }
                                  className="shrink-0 rounded p-1 text-gray-400 hover:bg-red-50 hover:text-red-600 disabled:opacity-40"
                                >
                                  <FaTrash className="text-xs" />
                                </button>
                              </li>
                            ) : null,
                        )}
                      </ul>
                      <div className="flex flex-wrap items-end gap-2">
                        <div className="min-w-[140px] flex-1">
                          <label className="mb-1 block text-xs text-gray-600">Link title</label>
                          <input
                            type="text"
                            disabled={disabled}
                            value={openLessonId === lesson.id ? attachLinkDraft.title : ''}
                            onChange={(e) =>
                              setAttachLinkDraft((d) => ({ ...d, title: e.target.value }))
                            }
                            className={inputPlanner}
                            placeholder="Handout / PDF"
                          />
                        </div>
                        <div className="min-w-[180px] flex-[2]">
                          <label className="mb-1 block text-xs text-gray-600">URL</label>
                          <input
                            type="url"
                            disabled={disabled}
                            value={openLessonId === lesson.id ? attachLinkDraft.url : ''}
                            onChange={(e) =>
                              setAttachLinkDraft((d) => ({ ...d, url: e.target.value }))
                            }
                            className={inputPlanner}
                            placeholder="https://…"
                          />
                        </div>
                        <Button
                          type="button"
                          size="sm"
                          variant="outline"
                          disabled={disabled || !attachLinkDraft.url.trim()}
                          className="gap-1"
                          onClick={() => {
                            const title = attachLinkDraft.title.trim() || 'Link';
                            const url = attachLinkDraft.url.trim();
                            if (!url) return;
                            const list = [...(lesson.attachments || [])];
                            list.push({
                              id: `att-${Date.now()}`,
                              title,
                              url,
                              kind: 'link',
                            });
                            updateLesson(lesson.id, { attachments: list });
                            setAttachLinkDraft({ title: '', url: '' });
                          }}
                        >
                          <FaLink className="text-xs" />
                          Add link
                        </Button>
                        <label className="inline-flex">
                          <input
                            type="file"
                            className="hidden"
                            disabled={
                              disabled || !courseId || uploadBusyLessonId === lesson.id
                            }
                            onChange={async (ev) => {
                              const file = ev.target.files?.[0];
                              ev.target.value = '';
                              if (!file || !courseId) return;
                              setUploadBusyLessonId(lesson.id);
                              try {
                                const safe = String(file.name || 'file')
                                  .replace(/[^\w.-]+/g, '_')
                                  .slice(0, 120);
                                const path = `courses/lessons/files/${courseId}__${lesson.id}__${Date.now()}_${safe}`;
                                const r = storageRef(storage, path);
                                await uploadBytes(r, file);
                                const url = await getDownloadURL(r);
                                const list = [...(lesson.attachments || [])];
                                list.push({
                                  id: `att-${Date.now()}`,
                                  title: file.name || 'File',
                                  url,
                                  kind: 'file',
                                });
                                updateLesson(lesson.id, { attachments: list });
                              } catch (e) {
                                console.error(e);
                                window.alert(
                                  'Upload failed. Check you are signed in and try again.',
                                );
                              } finally {
                                setUploadBusyLessonId(null);
                              }
                            }}
                          />
                          <span
                            className={`inline-flex cursor-pointer items-center gap-1 rounded-md border border-gray-300 px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 ${
                              !courseId || uploadBusyLessonId === lesson.id
                                ? 'pointer-events-none opacity-40'
                                : ''
                            }`}
                          >
                            <FaUpload className="text-xs" />
                            {uploadBusyLessonId === lesson.id ? 'Uploading…' : 'Upload file'}
                          </span>
                        </label>
                      </div>
                      {!courseId ? (
                        <p className="mt-2 text-xs text-amber-700">
                          Save course setup first so uploads can use a course folder.
                        </p>
                      ) : null}
                      </div>
                    </div>

                    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                      <EditableField
                        label="Assignment file URL (legacy)"
                        value={lesson.assignmentUrl || ''}
                        onChange={(value) => updateLesson(lesson.id, { assignmentUrl: value })}
                      />
                      <EditableField
                        label="Tutorial link (legacy)"
                        value={lesson.tuteUrl || ''}
                        onChange={(value) => updateLesson(lesson.id, { tuteUrl: value })}
                      />
                    </div>

                    <EditableArea
                      label="Slide & handout visuals"
                      value={lesson.imageRequirements || ''}
                      onChange={(value) =>
                        updateLesson(lesson.id, { imageRequirements: value })
                      }
                      rows={2}
                    />

                    <EditableArea
                      label="Image prompt (for AI cover)"
                      value={lesson.imagePrompt || ''}
                      onChange={(value) => updateLesson(lesson.id, { imagePrompt: value })}
                      rows={2}
                    />
                  </div>
                ) : null}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
};

const LessonImagesEditor = ({
  lessons,
  disabled,
  onChange,
  imageJobs,
  onGenerate,
  onRegenerate,
  onUpload,
}) => {
  const updateLesson = (lessonId, patch) => {
    onChange(
      lessons.map((lesson) =>
        lesson.id === lessonId ? { ...lesson, ...patch } : lesson,
      ),
      'images',
    );
  };

  return lessons.length === 0 ? (
    <EmptyState message="Generate lessons before creating lesson images." />
  ) : (
    <div className="space-y-4">
      <p className="text-xs text-slate-600">
        Previews use a <strong>16:9</strong> landscape frame—ideal for desktop. Upload or generate wide images for
        best results.
      </p>
      {lessons.map((lesson, index) => (
        <div
          key={lesson.id || index}
          className="rounded-xl border border-slate-200/90 bg-white p-3 shadow-sm ring-1 ring-slate-900/[0.03] sm:p-4"
        >
          <JobStatusPanel
            title={`${lesson.sessionLabel || `Session ${index + 1}`} image job`}
            job={imageJobs?.[lesson.id]}
          />
          <div className="grid grid-cols-1 gap-4 lg:grid-cols-[minmax(0,320px)_minmax(0,1fr)] lg:items-start">
            <div className="relative aspect-video w-full max-w-full overflow-hidden rounded-lg border border-slate-200/80 bg-gradient-to-br from-slate-100 to-slate-200 shadow-inner sm:max-w-lg">
              {lesson.imageUrl ? (
                <img
                  src={lesson.imageUrl}
                  alt={lesson.title || `Lesson ${index + 1}`}
                  className="h-full w-full object-cover object-center"
                />
              ) : (
                <div className="flex h-full min-h-[120px] flex-col items-center justify-center gap-2 px-4 text-center text-sm text-slate-500">
                  <FaFileImage className="text-2xl text-slate-400" />
                  <span>Landscape preview (16:9)</span>
                </div>
              )}
            </div>

            <div className="space-y-3">
              <div className="flex items-center justify-between gap-3">
                <div>
                  <p className="font-medium text-gray-900">
                    {lesson.sessionLabel || `Session ${index + 1}`}
                  </p>
                  <p className="text-sm text-gray-500">{lesson.title || 'Untitled lesson'}</p>
                </div>
                <div className="flex flex-wrap gap-2">
                  <Button size="sm" onClick={() => onGenerate(lesson.id)} disabled={disabled} className="gap-2">
                    <FaMagic className="text-xs" />
                    Generate
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => onRegenerate(lesson.id)} disabled={disabled} className="gap-2">
                    <FaRedo className="text-xs" />
                    Regenerate
                  </Button>
                  <label className="inline-flex">
                    <input
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={(event) => {
                        const file = event.target.files?.[0];
                        if (file) onUpload(lesson.id, file);
                      }}
                    />
                    <span className="inline-flex items-center justify-center rounded-md border border-gray-300 px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 cursor-pointer gap-2">
                      <FaUpload className="text-xs" />
                      Replace
                    </span>
                  </label>
                </div>
              </div>

              <EditableArea
                label="Image Prompt"
                value={lesson.imagePrompt || ''}
                onChange={(value) => updateLesson(lesson.id, { imagePrompt: value })}
              />
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

const CoverImageEditor = ({
  courseName,
  academicLevel,
  coverImage,
  disabled,
  onChange,
  onRegenerate,
  onUpload,
}) => (
  <div className="mb-6">
    <p className="mb-3 text-xs font-medium uppercase tracking-wide text-slate-600">
      Course cover · desktop landscape (16:9)
    </p>
    <div className="grid grid-cols-1 gap-5 lg:grid-cols-[minmax(0,1fr)_minmax(0,340px)] lg:items-start">
      <div className="relative mx-auto w-full max-w-4xl">
        <div className="pointer-events-none absolute -inset-1 rounded-2xl bg-gradient-to-br from-pink-500/20 via-fuchsia-500/15 to-amber-400/20 blur-sm" />
        <div className="relative overflow-hidden rounded-2xl border border-slate-200/90 bg-slate-900 shadow-2xl ring-1 ring-black/5 aspect-video">
          {coverImage?.url ? (
            <img
              src={coverImage.url}
              alt=""
              className="h-full w-full object-cover object-center"
            />
          ) : (
            <div
              className="h-full w-full bg-gradient-to-br from-slate-800 via-slate-900 to-fuchsia-950"
              style={{
                backgroundImage:
                  'radial-gradient(ellipse 120% 80% at 20% 0%, rgba(99,102,241,0.25), transparent 50%), radial-gradient(ellipse 100% 60% at 100% 100%, rgba(244,114,182,0.12), transparent 45%)',
              }}
            />
          )}
          <div className="pointer-events-none absolute right-3 top-3 rounded-full bg-black/35 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-white/90 backdrop-blur-sm">
            {coverImage?.source === 'course' ? 'Course cover' : '16:9'}
          </div>
          <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/90 via-black/55 to-transparent px-4 pb-4 pt-14 sm:px-6 sm:pb-5 sm:pt-20">
            {!coverImage?.url ? (
              <p className="mb-2 flex items-center gap-2 text-xs text-pink-200/90">
                <FaFileImage className="shrink-0" />
                <span>Generate or upload a landscape image — title stays in this safe area.</span>
              </p>
            ) : null}
            <p className="line-clamp-2 text-balance text-lg font-semibold leading-snug tracking-tight text-white sm:text-xl">
              {courseName || 'Course title'}
            </p>
            {academicLevel ? (
              <p className="mt-1 line-clamp-1 text-sm text-white/85">{academicLevel}</p>
            ) : null}
            <p className="mt-2 text-[10px] font-medium uppercase tracking-widest text-white/50">
              AI Lesson Planner · draft preview
            </p>
          </div>
        </div>
      </div>

      <div className="space-y-3 rounded-xl border border-slate-200/80 bg-slate-50/80 p-3 shadow-sm sm:p-4">
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" size="sm" onClick={onRegenerate} disabled={disabled} className="gap-2">
            <FaRedo className="text-xs" />
            Regenerate cover
          </Button>
          <label className="inline-flex">
            <input
              type="file"
              accept="image/*"
              className="hidden"
              disabled={disabled}
              onChange={(event) => {
                const file = event.target.files?.[0];
                if (file) onUpload(file);
              }}
            />
            <span className={`inline-flex items-center justify-center gap-2 rounded-md border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 ${disabled ? 'cursor-not-allowed opacity-60' : 'cursor-pointer'}`}>
              <FaUpload className="text-xs" />
              Upload landscape image
            </span>
          </label>
        </div>
        <EditableArea
          label="Cover image prompt"
          value={coverImage?.prompt || ''}
          onChange={(value) => onChange({ prompt: value })}
          rows={3}
        />
      </div>
    </div>
  </div>
);

const AssignmentsEditor = ({ lessons, disabled, onChange, onRegenerate }) => {
  const updateLessonAssignment = (lessonId, patch) => {
    onChange(
      lessons.map((lesson) =>
        lesson.id === lessonId
          ? {
              ...lesson,
              assignment: {
                ...(lesson.assignment || {}),
                ...patch,
              },
            }
          : lesson,
      ),
      'assignments',
    );
  };

  return lessons.length === 0 ? (
    <EmptyState message="Generate lessons before creating assignments." />
  ) : (
    <div className="space-y-4">
      {lessons.map((lesson, index) => (
        <div
          key={lesson.id || index}
          className="space-y-3 rounded-md border border-gray-200 p-3 sm:space-y-4 sm:p-4"
        >
          <div className="flex items-center justify-between gap-3">
            <div>
              <p className="font-medium text-gray-900">Lesson {index + 1}</p>
              <p className="text-sm text-gray-500">{lesson.title || 'Untitled lesson'}</p>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={() => onRegenerate(lesson.id)}
              disabled={disabled}
              className="gap-2"
            >
              <FaRedo className="text-xs" />
              Regenerate Assignment
            </Button>
          </div>

          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <EditableField
              label="Assignment Title"
              value={lesson.assignment?.title || ''}
              onChange={(value) => updateLessonAssignment(lesson.id, { title: value })}
            />
            <EditableArea
              label="Brief"
              value={lesson.assignment?.brief || ''}
              onChange={(value) => updateLessonAssignment(lesson.id, { brief: value })}
            />
            <EditableArea
              label="Instructions"
              value={lesson.assignment?.instructions || ''}
              onChange={(value) =>
                updateLessonAssignment(lesson.id, { instructions: value })
              }
            />
            <EditableArea
              label="Deliverables"
              value={lesson.assignment?.deliverables || ''}
              onChange={(value) =>
                updateLessonAssignment(lesson.id, { deliverables: value })
              }
            />
          </div>

          <EditableArea
            label="Rubric"
            value={lesson.assignment?.rubric || ''}
            onChange={(value) => updateLessonAssignment(lesson.id, { rubric: value })}
          />
        </div>
      ))}
    </div>
  );
};

const EditableField = ({ label, value, onChange, type = 'text' }) => (
  <div>
    <label className="mb-1.5 block text-sm font-medium text-gray-700">{label}</label>
    <input
      type={type}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className={inputPlanner}
    />
  </div>
);

const EditableArea = ({ label, value, onChange, rows = 3 }) => (
  <div>
    <label className="mb-1.5 block text-sm font-medium text-gray-700">{label}</label>
    <textarea
      value={value}
      onChange={(e) => onChange(e.target.value)}
      rows={rows}
      className={textareaPlanner}
    />
  </div>
);

const EmptyState = ({ message }) => (
  <div className="rounded-lg border border-dashed border-gray-300 bg-gray-50/50 px-3 py-8 text-center text-sm text-gray-500 sm:px-4 sm:py-10">
    {message}
  </div>
);

export default LessonPlanner;
